import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useMemo } from 'react';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { CustomError } from '../../../v2/components/error-boundary/CustomErrors';
import { useErrorBoundary } from '../../../v2/components/error-boundary/useErrorBoundary';
import { withErrorBoundary } from '../../../v2/components/error-boundary/withErrorBoundary';
import { PinnedFeaturesConfig } from '../../../v2/constants/PinnedFeaturesConfig';
import { useBadges } from '../../../v2/hooks/badges/useBadges';
import { useFeaturesForApiNames } from '../../../v2/hooks/features/useFeatures';
import { pinKeyFeatures } from '../../../v2/utils/featureUtils';
import SmartCrmFeatureTableHeaderCell from './SmartCrmFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SMART_CRM_LEVEL_TO_API_NAME_FOR_FEATURES = {
  ['FREE']: 'core-seats-free',
  // doesn't exist in merchandise, but can still query features for it
  ['STARTER']: 'core-seats-starter',
  ['PROFESSIONAL']: 'core-seats-professional',
  ['ENTERPRISE']: 'core-seats-enterprise'
};
const SMART_CRM_LEVEL_TO_API_NAME_FOR_BADGES = {
  ['FREE']: 'hubspot-free',
  // doesn't exist in merchandise, but can still query features for it
  ['STARTER']: 'crm-suite-starter-bundle',
  ['PROFESSIONAL']: 'core-seats-professional',
  ['ENTERPRISE']: 'core-seats-enterprise'
};
const buildFeatureTableHeaderData = ({
  productLevels,
  screen,
  badges
}) => {
  return productLevels.reduce((headers, level) => {
    var _badges$SMART_CRM_LEV;
    headers[SMART_CRM_LEVEL_TO_API_NAME_FOR_FEATURES[level]] = {
      Content: /*#__PURE__*/_jsx(SmartCrmFeatureTableHeaderCell, {
        apiName: SMART_CRM_LEVEL_TO_API_NAME_FOR_FEATURES[level],
        screen: screen,
        badgeName: badges === null || badges === void 0 || (_badges$SMART_CRM_LEV = badges[SMART_CRM_LEVEL_TO_API_NAME_FOR_BADGES[level]]) === null || _badges$SMART_CRM_LEV === void 0 ? void 0 : _badges$SMART_CRM_LEV.badgeName
      }, `${SMART_CRM_LEVEL_TO_API_NAME_FOR_FEATURES[level]}-header-cell`)
    };
    return headers;
  }, {});
};
const buildFeatureTableBodyData = ({
  features,
  productLevels,
  screen,
  staticDisplayRowCount
}) => {
  return {
    sharedData: {
      subscreen: screen,
      staticDisplayRowCount
    },
    sectionData: {
      'smart-crm': {
        apiNames: productLevels.map(level => SMART_CRM_LEVEL_TO_API_NAME_FOR_FEATURES[level]),
        features,
        LabelContent: /*#__PURE__*/_jsx(_Fragment, {})
      }
    }
  };
};
const SmartCrmFeatureTable = ({
  productLevels,
  screen
}) => {
  var _PinnedFeaturesConfig;
  const highlightedProductsForFeatures = useMemo(() => productLevels.map(productLevel => SMART_CRM_LEVEL_TO_API_NAME_FOR_FEATURES[productLevel]), [productLevels]);
  const highlightedProductsForBadges = useMemo(() => productLevels.map(productLevel => SMART_CRM_LEVEL_TO_API_NAME_FOR_BADGES[productLevel]), [productLevels]);
  const {
    badges
  } = useBadges({
    productApiNames: highlightedProductsForBadges
  });
  const {
    features,
    loading,
    error
  } = useFeaturesForApiNames(highlightedProductsForFeatures);
  const pinnedFeatures = useMemo(() => {
    if (!features || !features.length) return [];
    return pinKeyFeatures(features, 'CORE_PLATFORM_SEATS', PinnedFeaturesConfig);
  }, [features]);
  const {
    showBoundary
  } = useErrorBoundary();
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError(error.message || 'Error fetching features', {
      error,
      productLevels,
      screen,
      fingerprint: ['errorFetchingFeatures']
    }));
  }
  if (!features || !features.length) return null;
  const tableHeader = buildFeatureTableHeaderData({
    productLevels,
    screen,
    badges
  });
  const tableBody = buildFeatureTableBodyData({
    features: pinnedFeatures,
    productLevels,
    screen,
    staticDisplayRowCount: (_PinnedFeaturesConfig = PinnedFeaturesConfig['CORE_PLATFORM_SEATS']) === null || _PinnedFeaturesConfig === void 0 ? void 0 : _PinnedFeaturesConfig.length
  });
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: tableHeader,
    bodyData: tableBody,
    canViewNewSeats: false,
    isInbound25PricingPage: true
  });
};
export default withErrorBoundary(SmartCrmFeatureTable, {
  fallback: null,
  errorBoundaryName: 'SMART_CRM_FEATURE_TABLE'
});